import { Client, IMessage, StompSubscription } from "@stomp/stompjs";
import SockJS from "sockjs-client";
import { getItem, storageKey } from "@utilifiles/methods/storage/storage";

type RuntimeEnv = {
  VITE_API_URL?: string;
  WEBSOCKET_SERVICE_URL?: string;
};

function getRuntimeEnv(): RuntimeEnv {
  return ((window as any)._env_ || {}) as RuntimeEnv;
}

/** SockJS base from runtime env.js (not Vite build-time). */
function getWebsocketServiceUrl(): string {
  const env = getRuntimeEnv();
  if (env.WEBSOCKET_SERVICE_URL) {
    return String(env.WEBSOCKET_SERVICE_URL).replace(/\/$/, "");
  }
  // Fallback: same prefix as axios REST (VITE_API_URL) + service path
  const apiUrl = env.VITE_API_URL;
  if (typeof apiUrl === "string" && apiUrl.startsWith("/")) {
    return `${apiUrl.replace(/\/$/, "")}/web-socket-service`;
  }
  return "/web-socket-service";
}

export function rawJwt(token: string): string {
  return token.trim().replace(/^Bearer\s+/i, "").trim();
}

export function getAccessToken(): string | null {
  const tokenBag = getItem(storageKey.TOKEN);
  const fromObj = tokenBag?.accessToken;
  if (fromObj) return rawJwt(String(fromObj));
  const raw = window.localStorage.getItem(storageKey.accessToken);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed === "string") return rawJwt(parsed);
    if (parsed?.accessToken) return rawJwt(String(parsed.accessToken));
  } catch {
    return rawJwt(raw);
  }
  return null;
}

function resolveWsBaseUrl(): string {
  const configured = getWebsocketServiceUrl();
  if (configured.startsWith("http://") || configured.startsWith("https://")) {
    return configured.replace(/\/$/, "");
  }
  return `${window.location.origin}${configured.startsWith("/") ? configured : `/${configured}`}`.replace(
    /\/$/,
    "",
  );
}

export type MessengerHandlers = {
  onMessage: (frame: IMessage) => void;
  onReceipt: (frame: IMessage) => void;
  onConnect?: () => void;
  onDisconnect?: () => void;
  onError?: (error: unknown) => void;
};

export type MessengerStompHandle = {
  client: Client;
  activate: () => void;
  deactivate: () => Promise<void>;
  isConnected: () => boolean;
  publishSend: (body: object) => void;
  publishDelivered: (messageId: number) => void;
  publishRead: (conversationId: number, lastReadMessageId?: number) => void;
};

export function createMessengerStomp(
  handlers: MessengerHandlers,
): MessengerStompHandle {
  let messagesSub: StompSubscription | null = null;
  let receiptsSub: StompSubscription | null = null;

  const client = new Client({
    reconnectDelay: 5000,
    heartbeatIncoming: 10000,
    heartbeatOutgoing: 10000,
    webSocketFactory: () => {
      const token = getAccessToken();
      if (!token) {
        throw new Error("No access token for messenger WebSocket");
      }
      const base = resolveWsBaseUrl();
      const url = `${base}/ws-websocket?access_token=${encodeURIComponent(token)}`;
      console.log("[messenger] SockJS URL", url);
      return new SockJS(url) as WebSocket;
    },
    beforeConnect: () => {
      const token = getAccessToken();
      if (!token) return;
      client.connectHeaders = {
        Authorization: `Bearer ${token}`,
      };
    },
    onConnect: () => {
      messagesSub = client.subscribe("/user/queue/messages", handlers.onMessage);
      receiptsSub = client.subscribe("/user/queue/receipts", handlers.onReceipt);
      handlers.onConnect?.();
    },
    onDisconnect: () => {
      messagesSub = null;
      receiptsSub = null;
      handlers.onDisconnect?.();
    },
    onStompError: (frame) => {
      handlers.onError?.(frame);
    },
    onWebSocketError: (event) => {
      handlers.onError?.(event);
    },
  });

  const publish = (destination: string, body: object) => {
    if (!client.connected) return;
    client.publish({
      destination,
      body: JSON.stringify(body),
    });
  };

  return {
    client,
    activate: () => client.activate(),
    deactivate: async () => {
      try {
        messagesSub?.unsubscribe();
        receiptsSub?.unsubscribe();
      } catch {
        /* ignore */
      }
      messagesSub = null;
      receiptsSub = null;
      await client.deactivate();
    },
    isConnected: () => !!client.connected,
    publishSend: (body) => publish("/app/chat.send", body),
    publishDelivered: (messageId) =>
      publish("/app/chat.delivered", { messageId }),
    publishRead: (conversationId, lastReadMessageId) =>
      publish("/app/chat.read", {
        conversationId,
        ...(lastReadMessageId != null ? { lastReadMessageId } : {}),
      }),
  };
}
